import React from 'react';

/**
 * Available skeleton type variants
 */
export type SkeletonType = 'text' | 'avatar' | 'card' | 'table' | 'button';

/**
 * Props for the LoadingSkeleton component
 */
export interface LoadingSkeletonProps {
  /** The type of skeleton to render */
  type: SkeletonType;
  /** Custom width (CSS value, e.g., '100%', '200px', '12rem') */
  width?: string;
  /** Custom height (CSS value, e.g., '1rem', '40px') */
  height?: string;
  /** Number of lines for text variant (default: 1) */
  lines?: number;
  /** Number of rows for table variant (default: 5) */
  rows?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Base shimmer animation classes for skeleton elements
 */
const shimmerClasses =
  'animate-shimmer bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 bg-[length:200%_100%] dark:from-neutral-800 dark:via-neutral-700 dark:to-neutral-800';

/**
 * LoadingSkeleton component for displaying loading placeholders
 *
 * Supports multiple variants:
 * - text: Single or multiple lines of text placeholders
 * - avatar: Circular placeholder for profile images
 * - card: Rectangular card placeholder
 * - table: Table with multiple rows and alternating column widths
 * - button: Pill-shaped button placeholder
 */
export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({
  type,
  width,
  height,
  lines = 1,
  rows = 5,
  className = '',
}) => {
  const renderTextSkeleton = () => {
    const textLines = [];
    for (let i = 0; i < lines; i++) {
      // Vary width for multi-line text to look more natural
      const lineWidth = lines > 1 && i === lines - 1 ? '60%' : width || '100%';
      textLines.push(
        <div
          key={i}
          className={`${shimmerClasses} rounded ${className}`}
          style={{
            width: lineWidth,
            height: height || '1rem',
          }}
          role="presentation"
          aria-hidden="true"
        />
      );
    }
    return (
      <div className="space-y-2" role="status" aria-label="Loading text">
        {textLines}
      </div>
    );
  };

  const renderAvatarSkeleton = () => (
    <div
      className={`${shimmerClasses} rounded-full ${className}`}
      style={{
        width: width || '3rem',
        height: height || width || '3rem',
      }}
      role="status"
      aria-label="Loading avatar"
    />
  );

  const renderCardSkeleton = () => (
    <div
      className={`${shimmerClasses} rounded-lg ${className}`}
      style={{
        width: width || '100%',
        height: height || '8rem',
      }}
      role="status"
      aria-label="Loading card"
    />
  );

  const renderTableSkeleton = () => {
    const tableRows = [];
    // Column width patterns for visual variety
    const columnPatterns = [
      ['15%', '25%', '20%', '25%', '15%'],
      ['20%', '30%', '15%', '20%', '15%'],
    ];

    for (let i = 0; i < rows; i++) {
      const pattern = columnPatterns[i % 2];
      tableRows.push(
        <div
          key={i}
          className="flex items-center gap-4 py-3 border-b border-neutral-200 dark:border-neutral-700 last:border-b-0"
        >
          {pattern.map((colWidth, colIndex) => (
            <div
              key={colIndex}
              className={`${shimmerClasses} rounded`}
              style={{
                width: colWidth,
                height: height || '1rem',
              }}
            />
          ))}
        </div>
      );
    }

    return (
      <div
        className={`${className}`}
        style={{ width: width || '100%' }}
        role="status"
        aria-label="Loading table"
      >
        {/* Table header skeleton */}
        <div className="flex items-center gap-4 py-3 border-b-2 border-neutral-300 dark:border-neutral-600 mb-1">
          {columnPatterns[0].map((colWidth, colIndex) => (
            <div
              key={colIndex}
              className={`${shimmerClasses} rounded`}
              style={{
                width: colWidth,
                height: height || '1rem',
              }}
            />
          ))}
        </div>
        {tableRows}
      </div>
    );
  };

  const renderButtonSkeleton = () => (
    <div
      className={`${shimmerClasses} rounded-full ${className}`}
      style={{
        width: width || '6rem',
        height: height || '2.5rem',
      }}
      role="status"
      aria-label="Loading button"
    />
  );

  switch (type) {
    case 'text':
      return renderTextSkeleton();
    case 'avatar':
      return renderAvatarSkeleton();
    case 'card':
      return renderCardSkeleton();
    case 'table':
      return renderTableSkeleton();
    case 'button':
      return renderButtonSkeleton();
    default:
      return renderTextSkeleton();
  }
};

export default LoadingSkeleton;
